import { Box, Container, Divider, Grid } from "@mui/material";
import { useSlugValidation } from "@platformx/authoring-apis";
import {
  frameDocumentType,
  getContentUrlPath,
  getCurrentLang,
  getSubDomain,
  getTitleMax,
  SectionWrapper,
  ShowToastError,
  ShowToastSuccess,
  SlugFieldInput,
  SlugFieldLabel,
} from "@platformx/utilities";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { SectionProps } from "../../utils/types";
import { CreateHeader } from "../common/CreateHeader/CreateHeader";
import DynamicSectionComponent from "../DynamicSectionComponent";
import { useCustomStyle } from "./DynamicForm.style";

// The title field's maxLength must match the site-wide 255-char title limit
// (see @platformx/utilities getTitleMax) regardless of what the backend schema
// declares for it — dynamic/schema-based content types are not excluded.
const applyTitleFieldMaxLength = (fields: any[] = []) => {
  const max = getTitleMax();
  return fields?.map((field) => {
    if (field?.name?.toLowerCase() !== "title") return field;
    const message = `${max} characters max`;
    const hasMaxLength = field?.validations?.some((v: any) => v.type === "maxLength");
    const validations = hasMaxLength
      ? field.validations.map((v: any) =>
          v.type === "maxLength" ? { ...v, value: max, message } : v,
        )
      : [...(field?.validations || []), { type: "maxLength", value: max, message }];
    return { ...field, validations };
  });
};

export const DynamicComponent = ({
  contentType,
  template,
  handleSave,
  handlePublish,
  contentInstance,
  setContentInstance,
  isEdit,
  type,
  // setPageState,
  listUrl,
}) => {
  const { t } = useTranslation();
  const classes = useCustomStyle();
  const navigate = useNavigate();
  const slugValidation = useSlugValidation(contentType);
  const originalSlugRef = useRef<string | null>(null);

  const visibleFields =
    applyTitleFieldMaxLength(template?.fields)?.filter((field) => field?.is_show !== false) || [];

  // Group only visible fields based on index values
  const groupedFields = visibleFields?.reduce((result, field) => {
    const { index, ...rest } = field;
    const existingGroup = result?.find((group) => group.index === index);
    if (existingGroup) {
      existingGroup.fields.push(rest);
    } else {
      result.push({
        index,
        title: template?.form_groups?.find((x) => x.index === index)?.title,
        description: template?.form_groups?.find((x) => x.index === index)?.description,
        fields: [rest],
      });
    }
    return result;
  }, []);

  const seoSectionIndex = groupedFields?.findIndex((group) => /^seo$/i.test(group?.title || ""));

  // Some schemas (e.g. Profile) declare a slug-named field outside the SEO
  // section (e.g. under "Title & Description") — drop it there so slug is only
  // ever edited once, from the SEO section. Only do this when an SEO section
  // actually exists; otherwise there'd be nowhere left for the field to live,
  // silently making that content type's slug unreachable.
  if (seoSectionIndex >= 0) {
    groupedFields?.forEach((group) => {
      if (/^seo$/i.test(group?.title || "")) return;
      group.fields = group.fields?.filter((field) => !/^slug$/i.test(field?.name || ""));
    });
  }

  // Some schemas (e.g. Profile) already declare their own slug-named field inside
  // the SEO section — in that case leave the section entirely schema-driven and
  // don't inject a duplicate.
  const seoSectionHasOwnSlugField = groupedFields?.[seoSectionIndex]?.fields?.some((field) =>
    /^slug$/i.test(field?.name || ""),
  );
  const showGeneratedSlugField = seoSectionIndex >= 0 && !seoSectionHasOwnSlugField;
  const isCollection = template?.schema_document_type === "collection";

  useEffect(() => {
    if (slugValidation.isSubmitCheck) return;
    if (slugValidation.status === "unique") {
      ShowToastSuccess(t("slug_available", { docType: template?.title }));
    } else if (slugValidation.status === "not_unique") {
      ShowToastError(t("slug_not_unique", { docType: template?.title }));
    }
  }, [slugValidation.status, slugValidation.isSubmitCheck]);

  const handleSlugChange = (event) => {
    const { value } = event.target;
    setContentInstance((prev) => ({ ...prev, slug: value }));
    slugValidation.invalidate();
  };

  const handleSlugBlur = () => {
    slugValidation.validate(contentInstance?.slug, {
      excludeValue: originalSlugRef.current || "",
      schema_document_type: frameDocumentType(template?.schema_document_type || "content"),
    });
  };

  // Capture the fetched slug once, on mount — never overwritten again, so later
  // edits still get compared against the original stored value. Gated on
  // hasCapturedOriginalSlug (not "is contentInstance.slug truthy"): a legacy
  // document can have a genuinely empty stored slug, and by the time this
  // component mounts in edit mode contentInstance is already the real fetched
  // data (DynamicComponentContainer only renders it once loading finishes) —
  // so the empty string is itself a valid baseline to capture immediately.
  // Waiting for the first non-empty value would instead capture whatever the
  // user's first edit produces; if that edit arrives as a single paste (not
  // incremental typing), it would wrongly become its own "original" and
  // silently skip the uniqueness check for a brand-new, colliding slug.
  // Still gated on isEdit: in create mode there is no stored value at all, so
  // capturing on mount there would do the same thing for a typed-from-scratch
  // slug.
  const hasCapturedOriginalSlug = useRef(false);
  useEffect(() => {
    if (isEdit && !hasCapturedOriginalSlug.current) {
      hasCapturedOriginalSlug.current = true;
      originalSlugRef.current = contentInstance?.slug || "";
    }
  }, [isEdit, contentInstance?.slug]);

  const currentSlug = contentInstance?.slug || "";
  const slugField = {
    value: currentSlug,
    onChange: handleSlugChange,
    onBlur: handleSlugBlur,
    canonicalUrl:
      currentSlug && !isCollection
        ? `${getSubDomain()}/${getCurrentLang()}${getContentUrlPath({ tagName: contentType, slug: currentSlug })}`
        : "",
  };

  const checkSlugThenRun = async (run: () => void) => {
    // Only content types that actually show a slug field (an SEO section
    // exists — generated or schema-declared) require a value; other content
    // types never had a slug field to fill in, so currentSlug staying empty
    // for them is normal, not a cleared-out required field.
    if (seoSectionIndex >= 0 && !currentSlug) {
      ShowToastError(t("slug_required"));
      return;
    }
    if (currentSlug) {
      const isUnique = await slugValidation.checkNow(currentSlug, {
        excludeValue: originalSlugRef.current || "",
        schema_document_type: frameDocumentType(template?.schema_document_type || "content"),
      });
      if (!isUnique) {
        ShowToastError(t("slug_url_not_unique", { docType: template?.title }));
        return;
      }
      // The value just cleared uniqueness — advance the baseline now so a later
      // save/publish in this same session (e.g. Publish right after Save as
      // Draft) excludes against the value the document now actually owns,
      // instead of re-checking it against itself and failing as "not unique".
      // Refs aren't subject to the read/write races this rule guards against;
      // currentSlug was already captured (as the excludeValue check above)
      // before this await.
      // eslint-disable-next-line require-atomic-updates
      originalSlugRef.current = currentSlug;
    }
    run();
  };

  //ReactHookForm setup
  const form = useForm({
    defaultValues: contentInstance,
    mode: "onBlur",
  });

  const { register, formState, clearErrors, handleSubmit, control } = form;
  const { errors } = formState;

  const onError = (errorss, e) => {
    //function to focus errors on custom elements like image picker, tags
    e.preventDefault();
    if (Object.keys(errorss).length > 0) {
      const goToError = document.getElementById(`${Object.keys(errorss)[0]}_id`);
      if (goToError) goToError.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };
  const guardedPublish = (data, e) => checkSlugThenRun(() => handlePublish(data, e));
  return (
    <form onSubmit={handleSubmit(guardedPublish, onError)}>
      <Box>
        <CreateHeader
          createText={
            isEdit ? `${t("edit")} ${template?.title}` : `${t("create")} ${template?.title}`
          }
          publishText={t("publish")}
          saveText={t("save_as_draft")}
          previewText={t("preview")}
          toolTipText={t("preview_tooltip")}
          saveVariant='outlined'
          category={type}
          subCategory={contentType}
          id={""}
          hasPreviewButton={false}
          handleReturn={() => {
            navigate(`/${listUrl}/${contentType}`);
          }}
          handlePublish={() => {
            // setPageState("publish");
          }}
          handleSaveOrPublish={() => {
            // setPageState("save");
            checkSlugThenRun(() => handleSave("update"));
          }}
          editText={""}
          isQuiz={false}
          hasPublishButton={false}
          hasSaveButton={false}
          showPreview={false}
          workflow={{ enable: false }}
          hasTimerState={false}
          lastModifiedDate={""}
          isFeatured={false}
          setIsFeatured={() => {}}
        />
        <Divider></Divider>
      </Box>
      <Box
        sx={{
          height: { md: "calc(-126px + 100vh)", xs: "calc(-45px + 100vh)" },
          overflowY: "auto",
        }}>
        {groupedFields?.length > 0 &&
          groupedFields.map((section: SectionProps, index: number) => {
            return (
              <Box className={classes.mainStyleWrapper} key={section?.index}>
                <SectionWrapper
                  number={section.index}
                  title={section.title}
                  subTitle={section.description}
                  titleVariant={"h5bold"}
                  subTitleVariant={"p4regular"}>
                  <DynamicSectionComponent
                    fields={section.fields}
                    state={contentInstance}
                    setState={setContentInstance}
                    errors={errors}
                    register={register}
                    clearErrors={clearErrors}
                    isEdit={isEdit}
                    control={control}
                    title={template?.title}
                  />
                  {showGeneratedSlugField && index === seoSectionIndex && (
                    <Container>
                      <Grid container spacing={5} rowSpacing={2}>
                        <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiled'>
                          <SlugFieldLabel />
                        </Grid>
                        <Grid item xs={12} sm={7} md={7} lg={7} className='textFiled'>
                          <SlugFieldInput
                            value={slugField.value}
                            onChange={slugField.onChange}
                            onBlur={slugField.onBlur}
                            canonicalUrl={slugField.canonicalUrl}
                          />
                        </Grid>
                      </Grid>
                    </Container>
                  )}
                </SectionWrapper>
              </Box>
            );
          })}
      </Box>
    </form>
  );
};
